import { Layers, MailWarning, Sparkles, Sunrise, type LucideIcon } from 'lucide-react';
import { noticeWords, type NoticeKind } from '../screens/account/notice-words';

/** How loud a notice's mark is: the accent for work waiting, amber for a hold, red for something broken. */
export type NoticeTone = 'accent' | 'warning' | 'danger';

/** The kinds the bell shows that the notice choices do not list (they have no switch of their own). */
const unlisted: Record<string, LucideIcon> = {
  quarantine: MailWarning,
  learning: Sparkles,
  overview: Sunrise,
  provisional_job: Layers,
};

const tones: Record<string, NoticeTone> = {
  breaker_open: 'danger',
  payment_failed: 'danger',
  credits_exhausted: 'warning',
  device: 'warning',
  quarantine: 'warning',
};

/** A notice's mark in the bell: the same picture its row has under "Choose what you get". */
export function noticeLook(kind: string | null | undefined): {
  icon: LucideIcon;
  tone: NoticeTone;
} {
  const k = kind ?? '';
  const icon = unlisted[k] ?? noticeWords(k as NoticeKind['kind']).icon;
  return { icon, tone: tones[k] ?? 'accent' };
}

/** The shortest a lead may be before its break is taken: "Note: …" is not a headline. */
const SHORTEST_LEAD = 12;

/**
 * A notice's sentence as the bell sets it: what happened as the line, the why and what to do under it in
 * grey. The ledger writes one sentence a notice (it is also the email's first line), with its why after a
 * colon, a semicolon or a full stop ("14 documents are waiting for review in AP invoices: the job is in
 * Manual, …"); the break is the first of those after a short lead. A sentence with none is all line.
 */
export function splitNotice(subject: string): { line: string; more: string | null } {
  const text = subject.trim();
  // A computer gone quiet: its name and the news as the line, the moment and what waits under it (the email
  // says it the same way, NoticeMailWords).
  const quiet = /^(Paige Scan on .+?) has not been heard from since (.+?)\. (.+)$/.exec(text);
  if (quiet)
    return {
      line: `${quiet[1]} has gone quiet`,
      more: `Last heard from ${quiet[2]}. ${sentence(quiet[3]!)}`,
    };
  const breaks = [': ', '; ', '. ']
    .map((mark) => text.indexOf(mark, SHORTEST_LEAD))
    .filter((at) => at > 0);
  if (breaks.length === 0) return { line: text, more: null };
  const at = Math.min(...breaks);
  const rest = text.slice(at + 2).trim();
  if (!rest) return { line: text, more: null };
  return { line: text.slice(0, at), more: sentence(rest) };
}

function sentence(text: string): string {
  const capital = text[0]!.toUpperCase() + text.slice(1);
  return /[.!?…]$/.test(capital) ? capital : `${capital}.`;
}
